import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, RefreshCcw, CheckCircle2, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsPanel() {
    const [cfg, setCfg] = useState(null);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [newKey, setNewKey] = useState('');

    const load = async () => {
        try {
            const [c, t] = await Promise.all([
                axios.get(`${API_URL}/api/admin/integrations/systemeio`, { withCredentials: true }),
                axios.get(`${API_URL}/api/admin/integrations/systemeio/tags`, { withCredentials: true }).catch(() => ({ data: { tags: [] } }))
            ]);
            setCfg(c.data);
            setTags(t.data?.tags || []);
        } catch (err) {
            toast.error('Failed to load integrations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                default_tags: cfg.default_tags || {},
                enabled: cfg.enabled !== false
            };
            if (newKey.trim()) payload.api_key = newKey.trim();
            const { data } = await axios.put(`${API_URL}/api/admin/integrations/systemeio`, payload, { withCredentials: true });
            setCfg(data);
            setNewKey('');
            toast.success('Settings saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/admin/integrations/systemeio/test`, {}, { withCredentials: true });
            toast.success(`Connected — ${data.tag_count} tag(s) in your systeme.io account`);
            const t = await axios.get(`${API_URL}/api/admin/integrations/systemeio/tags`, { withCredentials: true });
            setTags(t.data?.tags || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Test failed');
        } finally {
            setTesting(false);
        }
    };

    const updateTagMapping = (triggerKey, tagId) => {
        setCfg({ ...cfg, default_tags: { ...(cfg.default_tags || {}), [triggerKey]: tagId } });
    };

    if (loading) {
        return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#5B2D8E] mx-auto" /></div>;
    }

    return (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 space-y-8" data-testid="integrations-panel">
            <div>
                <p className="overline mb-2">Integrations</p>
                <h2 className="font-serif text-3xl text-[#1A1A2E]">systeme.io</h2>
                <p className="text-sm text-[#4A4A68] mt-2">
                    Sync buyers & signups into your systeme.io account and assign tags to trigger your funnels/email sequences.
                </p>
            </div>

            {/* API Key */}
            <div className="border-t border-[#E5E0D8] pt-6">
                <label className="block font-sans text-sm uppercase tracking-widest text-[#4A4A68] mb-2">
                    <KeyRound className="w-4 h-4 inline mr-2" />
                    Public API Key
                </label>
                {cfg?.has_api_key ? (
                    <p className="text-sm text-[#1A1A2E] mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Configured (ends in <code className="bg-[#F2EBE5] px-2 py-0.5 rounded">…{cfg.api_key_last4}</code>)
                    </p>
                ) : (
                    <p className="text-sm text-red-600 mb-3">No API key configured yet.</p>
                )}
                <div className="flex gap-2">
                    <input
                        type="password"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                        placeholder={cfg?.has_api_key ? 'Enter a new key to rotate…' : 'Paste your systeme.io Public API Key'}
                        className="flex-1"
                        data-testid="systemeio-api-key-input"
                    />
                    <button
                        onClick={testConnection}
                        disabled={testing || !cfg?.has_api_key}
                        className="btn-secondary text-xs flex items-center gap-2"
                        data-testid="test-systemeio-btn"
                    >
                        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                        Test
                    </button>
                </div>
                <p className="text-xs text-[#4A4A68] mt-2">
                    Get your key in systeme.io → Settings → MCP & API keys → Create.
                </p>
            </div>

            {/* Tag mapping */}
            <div className="border-t border-[#E5E0D8] pt-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-serif text-xl text-[#1A1A2E]">Trigger → Tag mapping</h3>
                        <p className="text-sm text-[#4A4A68]">Pick a systeme.io tag for each form or event. Each row tells you exactly where on the site that trigger fires.</p>
                    </div>
                    <button
                        onClick={testConnection}
                        disabled={testing}
                        className="btn-secondary text-xs flex items-center gap-2"
                    >
                        <RefreshCcw className="w-3 h-3" />
                        Refresh tags
                    </button>
                </div>
                {tags.length === 0 && (
                    <p className="text-sm text-[#4A4A68] italic">No tags found. Create tags inside systeme.io first, then Refresh.</p>
                )}
                <div className="space-y-4">
                    {(cfg?.trigger_list || Object.entries(cfg?.triggers || {}).map(([key, description]) => ({ key, label: key.replace(/_/g, ' '), description, source: '' }))).map(trigger => (
                        <div
                            key={trigger.key}
                            className="grid grid-cols-1 sm:grid-cols-[minmax(0,280px)_1fr] gap-3 items-start p-3 rounded-sm border border-[#E5E0D8] bg-[#F9F6F0]/40"
                            data-testid={`trigger-row-${trigger.key}`}
                        >
                            <div>
                                <p className="font-serif text-[#1A1A2E]">{trigger.label}</p>
                                {trigger.source && (
                                    <p className="text-[11px] uppercase tracking-widest text-[#5B2D8E] mt-1">{trigger.source}</p>
                                )}
                                <p className="text-xs text-[#4A4A68] mt-1">{trigger.description}</p>
                            </div>
                            <select
                                value={cfg?.default_tags?.[trigger.key] ?? ''}
                                onChange={(e) => updateTagMapping(trigger.key, e.target.value ? Number(e.target.value) : '')}
                                className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                data-testid={`trigger-tag-${trigger.key}`}
                            >
                                <option value="">— No tag —</option>
                                {tags.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            </div>

            {/* Enable toggle */}
            <div className="border-t border-[#E5E0D8] pt-6">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={cfg?.enabled !== false}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                        data-testid="systemeio-enabled"
                    />
                    <span className="text-sm text-[#1A1A2E]">Integration enabled (uncheck to pause all firing)</span>
                </label>
            </div>

            <button
                onClick={save}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
                data-testid="save-integrations-btn"
            >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save settings
            </button>

            {/* --- Resend (transactional emails) --- */}
            <ResendSection />

            {/* --- Stripe (payments) --- */}
            <StripeSection />
        </div>
    );
}

function ResendSection() {
    const [cfg, setCfg] = useState(null);
    const [newKey, setNewKey] = useState('');
    const [fromEmail, setFromEmail] = useState('');
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/integrations/resend`, { withCredentials: true });
            setCfg(data);
            setFromEmail(data.from_email || '');
        } catch (_err) {
            // silent — surface as error only when the admin tries to save
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { from_email: fromEmail.trim() || null, enabled: cfg?.enabled !== false };
            if (newKey.trim()) payload.api_key = newKey.trim();
            const { data } = await axios.put(`${API_URL}/api/admin/integrations/resend`, payload, { withCredentials: true });
            setCfg(data);
            setNewKey('');
            toast.success('Resend settings saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/admin/integrations/resend/test`, {}, { withCredentials: true });
            if (data.sent) {
                toast.success(`Test email sent to ${data.to}. Check your inbox (and spam).`);
            } else {
                toast.error('Resend returned an error — check backend logs. Free-tier accounts can only send to their own email until you verify a domain.');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Test send failed');
        } finally {
            setTesting(false);
        }
    };

    if (!cfg) return null;
    return (
        <div className="mt-10 pt-8 border-t border-[#E5E0D8]" data-testid="resend-section">
            <div className="flex items-center gap-3 mb-2">
                <h2 className="heading-3">Resend — transactional emails</h2>
                {cfg.has_api_key ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="w-3 h-3" /> Configured</span>
                ) : (
                    <span className="text-xs text-amber-700">Not configured</span>
                )}
            </div>
            <p className="text-sm text-[#4A4A68] mb-6">
                Password resets, purchase confirmations and welcome emails are sent through Resend.{' '}
                {cfg.source === 'env' && (
                    <>The current key is loaded from the pod env. Paste a new one below to override it live.</>
                )}
            </p>

            <div className="space-y-4 mb-4">
                <div>
                    <label className="block text-sm text-[#1A1A2E] mb-2">API key</label>
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-[#4A4A68]" />
                        <input
                            type="password"
                            placeholder={cfg.has_api_key ? `Current key ends in •••• ${cfg.api_key_last4}` : 'Paste your re_… key'}
                            value={newKey}
                            onChange={(e) => setNewKey(e.target.value)}
                            className="flex-1"
                            data-testid="resend-api-key"
                        />
                    </div>
                    <p className="text-xs text-[#4A4A68] mt-1">Get a key at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#5B2D8E] hover:underline">resend.com/api-keys</a>. Leave empty to keep the current key.</p>
                </div>
                <div>
                    <label className="block text-sm text-[#1A1A2E] mb-2">From email</label>
                    <input
                        type="email"
                        value={fromEmail}
                        onChange={(e) => setFromEmail(e.target.value)}
                        placeholder="team@methode4life.com"
                        className="w-full"
                        data-testid="resend-from-email"
                    />
                    <p className="text-xs text-[#4A4A68] mt-1">
                        Must belong to a domain you've verified in Resend. Until then, use <code>onboarding@resend.dev</code> (Resend's sandbox sender — you can only email your account owner address).
                    </p>
                </div>
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={cfg?.enabled !== false}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                        data-testid="resend-enabled"
                    />
                    <span className="text-sm text-[#1A1A2E]">Integration enabled (uncheck to silence all outgoing email)</span>
                </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <button
                    onClick={save}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2"
                    data-testid="save-resend-btn"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Resend settings
                </button>
                <button
                    onClick={sendTest}
                    disabled={testing || !cfg.has_api_key}
                    className="btn-secondary text-xs flex items-center gap-2"
                    data-testid="resend-test-btn"
                    title={!cfg.has_api_key ? 'Save an API key first' : 'Send yourself a probe email'}
                >
                    {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                    Send test email to me
                </button>
            </div>
        </div>
    );
}

function StripeSection() {
    const [cfg, setCfg] = useState(null);
    const [newKey, setNewKey] = useState('');
    const [useDirect, setUseDirect] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/integrations/stripe`, { withCredentials: true });
            setCfg(data);
            setUseDirect(!!data.use_direct);
        } catch (_err) {
            // silent
        }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { use_direct: useDirect };
            if (newKey.trim()) payload.api_key = newKey.trim();
            const { data } = await axios.put(`${API_URL}/api/admin/integrations/stripe`, payload, { withCredentials: true });
            setCfg(data);
            setNewKey('');
            toast.success('Stripe settings saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (!cfg) return null;
    const modeLabel = {
        direct: 'Your Stripe account (live routing)',
        emergent_proxy: 'Emergent test proxy (no money moves)',
        unconfigured: 'Not configured'
    }[cfg.mode] || cfg.mode;

    return (
        <div className="mt-10 pt-8 border-t border-[#E5E0D8]" data-testid="stripe-section">
            <div className="flex items-center gap-3 mb-2">
                <h2 className="heading-3">Stripe — payments</h2>
                {cfg.mode === 'direct' ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700" data-testid="stripe-mode-badge">
                        <CheckCircle2 className="w-3 h-3" /> Live via your account{cfg.livemode ? ' (live keys)' : ' (test keys)'}
                    </span>
                ) : cfg.mode === 'emergent_proxy' ? (
                    <span className="text-xs text-amber-700" data-testid="stripe-mode-badge">Using Emergent test proxy</span>
                ) : (
                    <span className="text-xs text-red-700" data-testid="stripe-mode-badge">Not configured</span>
                )}
            </div>
            <p className="text-sm text-[#4A4A68] mb-6">
                Currently routing: <strong>{modeLabel}</strong>.{' '}
                Paste your own Stripe secret key (<code>sk_test_…</code> or <code>sk_live_…</code>) and tick
                "Route payments through my Stripe account" to receive payouts in your own bank account.
            </p>

            <div className="space-y-4 mb-4">
                <div>
                    <label className="block text-sm text-[#1A1A2E] mb-2">Secret API key</label>
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-[#4A4A68]" />
                        <input
                            type="password"
                            placeholder={cfg.has_api_key ? `Saved key ends in •••• ${cfg.api_key_last4}` : 'Paste your sk_test_… or sk_live_… key'}
                            value={newKey}
                            onChange={(e) => setNewKey(e.target.value)}
                            className="flex-1"
                            data-testid="stripe-api-key"
                        />
                    </div>
                    <p className="text-xs text-[#4A4A68] mt-1">
                        Get a key at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[#5B2D8E] hover:underline">dashboard.stripe.com/apikeys</a>. Leave empty to keep the saved key.
                    </p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-[#F9F6F0] rounded-sm">
                    <input
                        type="checkbox"
                        checked={useDirect}
                        onChange={(e) => setUseDirect(e.target.checked)}
                        className="mt-1"
                        data-testid="stripe-use-direct"
                    />
                    <div className="flex-1">
                        <p className="text-sm text-[#1A1A2E]"><strong>Route payments through my Stripe account</strong></p>
                        <p className="text-xs text-[#4A4A68] mt-1">
                            When checked, every checkout goes to your Stripe account (and your bank). Unchecked → payments stay on the Emergent test proxy (safe for testing, nothing hits your account).
                        </p>
                    </div>
                </label>
            </div>

            <button
                onClick={save}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
                data-testid="save-stripe-btn"
            >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Stripe settings
            </button>
        </div>
    );
}
